import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AuthUserModel, Folder, Gender, HxAuthService, HxFileService, isoDate, MaritalStatus, SysFilename, TableRef } from 'hx-services';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { NzUploadChangeParam, NzUploadComponent, NzUploadFile } from 'ng-zorro-antd/upload';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NgFor, NgIf } from '@angular/common';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

@Component({
  selector: 'app-profile-edit',
  templateUrl: './profile-edit.component.html',
  styleUrls: ['./profile-edit.component.less'],
  imports: [TranslocoDirective, NzPageHeaderComponent, NzPageHeaderTitleDirective, NgIf, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzFormItemComponent, NzColDirective, NzFormControlComponent, NzUploadComponent, NzIconDirective, NgFor, NzFormLabelComponent, NzInputDirective, NzDatePickerComponent, NzRadioGroupComponent, NzRadioComponent, NzSelectComponent, NzOptionComponent, NzButtonComponent, NzWaveDirective, TranslocoPipe]
})
export class ProfileEditComponent implements OnInit {

  @Input() user!: AuthUserModel;
  @Output() save = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  isLoading = {
    avatarFile: false,
    signFile: false,
    save: false,
  };
  avatarUrl?: string;
  signUrl?: string;
  fileUploadUrl!: string;
  form = new FormGroup({
    iin: new FormControl<string | undefined>(undefined, [Validators.required]),
    email: new FormControl<string | undefined>(undefined),
    lastname: new FormControl<string | undefined>(undefined),
    firstname: new FormControl<string | undefined>(undefined),
    patronymic: new FormControl<string | undefined>(undefined),
    address: new FormControl<string | undefined>(undefined),
    birthdate: new FormControl<Date | null>(null),
    gender: new FormControl<Gender | undefined>(undefined, [Validators.required]),
    maritalStatus: new FormControl<MaritalStatus | undefined>(undefined),
    avatarFileId: new FormControl<number | undefined>(undefined),
    signFileId: new FormControl<number | undefined>(undefined),
  });

  files: NzUploadFile[] = [];

  constructor(
    private auth: HxAuthService,
    private keycloakService: KeycloakService,
    private fileService: HxFileService,
    private nt: NzNotificationService,
    private tr: TranslocoService,
  ) {
  }

  fileUploadHeadersFn: (file: NzUploadFile) => Observable<{}> = file => from(this.keycloakService.getToken()).pipe(map(token => ({Authorization: `Bearer ${token}`})));
  avatarDataFn: (file: NzUploadFile) => {} | Observable<{}> = file => {
    const fileName = file.name;
    const params: {
      folder?: string,
      temporary?: boolean,
      ref?: number,
      table?: string,
      file_name?: string,
    } = {
      folder: Folder.SYSTEM,
      file_name: SysFilename.avatar + fileName.substr(fileName.lastIndexOf('.')),
      temporary: true,
    };
    if (this.user.profileId) {
      params.ref = this.user.profileId;
      params.table = TableRef.hc_profiles;
    }
    return params;
  };
  signDataFn: (file: NzUploadFile) => {} | Observable<{}> = file => {
    const fileName = file.name;
    const params: {
      folder?: string,
      temporary?: boolean,
      ref?: number,
      table?: string,
      file_name?: string,
    } = {
      folder: Folder.SYSTEM,
      file_name: SysFilename.sign + fileName.substr(fileName.lastIndexOf('.')),
      temporary: true,
    };
    if (this.user.profileId) {
      params.ref = this.user.profileId;
      params.table = TableRef.hc_profiles;
    }
    return params;
  };

  ngOnInit() {
    this.fileUploadUrl = HxFileService.fileUploadUrl;
    let bir;
    if (this.user.birthdate) {
      bir = new Date(this.user.birthdate);
    } else {
      bir = new Date();
    }
    this.form.patchValue({
      iin: this.user.iin,
      email: this.user.email,
      lastname: this.user.lastname,
      firstname: this.user.firstname,
      patronymic: this.user.patronymic,
      address: this.user.address,
      birthdate: bir,
      gender: this.user.gender,
      maritalStatus: this.user.maritalStatus,
      avatarFileId: undefined,
      signFileId: undefined,
    });

    if (this.user.profileId) {
      this.fileService.getFilesByRef({ref: this.user.profileId, table: TableRef.hc_profiles, folder: Folder.SYSTEM}).then(files => {
        this.files = files.map(f => ({uid: f.guid, name: f.fileName, url: `${HxFileService.fileDownloadUrl}/${f.guid}`}));
        this.form.patchValue({ avatarFileId: files.find(f => f.fileName.startsWith(SysFilename.avatar))?.id,
        signFileId: files.find(f => f.fileName.startsWith(SysFilename.sign))?.id });
        this.avatarUrl = this.files.find(f => f.name.startsWith(SysFilename.avatar))?.url;
        this.signUrl = this.files.find(f => f.name.startsWith(SysFilename.sign))?.url;
      });
    }
  }

  saveProfile() {
    const formValue: any = {
      iin: this.form.value?.iin,
      email: this.form.value.email,
      lastname: this.form.value.lastname,
      firstname: this.form.value.firstname,
      patronymic: this.form.value.patronymic,
      address: this.form.value.address,
      birthdate: this.form.value.birthdate,
      gender: this.form.value.gender,
      maritalStatus: this.form.value.maritalStatus,
      avatarFileId: this.form.value.avatarFileId,
      signFileId: this.form.value.signFileId,
    };
    formValue.birthdate = isoDate(formValue.birthdate);
    console.log('save profile', formValue);

    // eslint-disable-next-line guard-for-in
    Object.values(this.form.controls).forEach(value => {
      value.markAsDirty();
      value.updateValueAndValidity();
    });
    if (this.form.valid) {
      this.isLoading.save = true;
      this.auth.saveProfile(formValue).subscribe(() => {
        this.nt.success(this.tr.translate('result.saved'), this.tr.translate('form.saved'));
        this.isLoading.save = false;
        this.save.emit();
      }, err => {
        this.isLoading.save = false;
      });
    }
  }

  onBackClicked() {
    this.form.reset();
    this.back.emit();
  }

  handleUploadChange(event: NzUploadChangeParam, fileName: 'sign' | 'avatar') {
    console.log('handleUploadChange', event, fileName);

    const uploadFilename = event.file.name;
    const suffix = uploadFilename.substr(uploadFilename.lastIndexOf('.'));
    if (fileName === SysFilename.avatar) {
      this.isLoading.avatarFile = event.type === 'start' || event.type === 'progress';
    } else if (fileName === SysFilename.sign) {
      this.isLoading.signFile = event.type === 'start' || event.type === 'progress';
    }
    const control: UntypedFormControl = this.form.controls[`${fileName}FileId`] as UntypedFormControl;
    if (event.type === 'success') {
      control.setValue(event.file.response.id);
      control.markAsDirty();
      const savedFile: NzUploadFile = {
        uid: event.file.response.guid,
        name: `${fileName}${suffix}`,
        url: `${HxFileService.fileDownloadUrl}/${event.file.response.guid}`,
      };
      this.files = this.files.filter(f => f.name !== `${fileName}${suffix}`).concat([savedFile]);
    } else if (event.type === 'removed') {
      control.setValue(undefined);
      control.markAsDirty();
      this.files = this.files.filter(f => f.name !== `${fileName}${suffix}`);
    }
    this.avatarUrl = this.files.find(f => f.name.startsWith(SysFilename.avatar))?.url;
    this.signUrl = this.files.find(f => f.name.startsWith(SysFilename.sign))?.url;
  }
}
